import SEO from "@/components/SEO";
import { PageHero, SectionHead, CTABand, Reveal } from "@/components/Ui";
import { RelatedLinks } from "@/pages/ServicePage";
import { MapPin } from "lucide-react";

const zones = [
  {
    ville: "Bernay et alentours",
    communes: "Bernay · Thiberville · Menneval · Fontaine-l'Abbé · Serquigny · Caorches-Saint-Nicolas · Saint-Martin-du-Tilleul",
    texte: "Notre secteur principal. Maison à rénover en centre-ville de Bernay, longère à restaurer à la campagne, extension ou dalle béton aux alentours : nous sommes sur place rapidement pour étudier votre projet et vous remettre un devis gratuit.",
  },
  {
    ville: "Évreux et sa métropole",
    communes: "Évreux et communes voisines",
    texte: "Construction neuve, gros œuvre, ouverture de mur porteur ou rénovation : nous intervenons régulièrement sur Évreux. Notre adresse est d'ailleurs à Évreux, rue Jean de la Fontaine — un déplacement rapide et sans frais cachés.",
  },
  {
    ville: "Les vallées : Beaumont-le-Roger et Brionne",
    communes: "Beaumont-le-Roger · Brionne · et les communes des vallées",
    texte: "Entre Bernay et le sud de l'Eure, les vallées de la Risle et de la Charentonne comptent de nombreux bâtis anciens. Rénovation de maçonnerie, rejointoiement, murets et terrasses font partie de nos interventions courantes dans ces communes.",
  },
  {
    ville: "Le Neubourg et le plateau",
    communes: "Le Neubourg · plateau du Neubourg",
    texte: "Extensions de maison, fondations, dalles et aménagements extérieurs : nous accompagnons les particuliers et professionnels du plateau du Neubourg pour leurs projets de construction et d'agrandissement.",
  },
  {
    ville: "Conches-en-Ouche et le centre de l'Eure",
    communes: "Conches-en-Ouche · centre du département",
    texte: "Dans le centre de l'Eure, nous réalisons allées en pavés, terrasses, dalles béton, murets et travaux de maçonnerie courante — comme notre chantier d'allée pavée avec bordures à Conches-en-Ouche.",
  },
  {
    ville: "Beuzeville et l'ouest de l'Eure",
    communes: "Beuzeville · Pont-Audemer · ouest du département",
    texte: "Nous intervenons aussi dans l'ouest de l'Eure : parement brique, rénovation de façades, murets et aménagements extérieurs — comme notre chantier de parement brique réalisé à Beuzeville, visible dans nos réalisations.",
  },
  {
    ville: "Louviers et Vernon",
    communes: "Louviers · Vernon · sud de l'Eure",
    texte: "Dans le sud du département, nous réalisons les travaux de maçonnerie, de gros œuvre et de rénovation des maisons — terrasses, allées pavées, bordures et escaliers extérieurs — avec la même exigence : un chantier préparé, un devis clair, un ouvrage durable.",
  },
  {
    ville: "Deauville et la Côte Fleurie",
    communes: "Deauville · Côte Fleurie · selon l'importance du chantier",
    texte: "Pour les chantiers d'envergure, nous intervenons jusqu'à Deauville : gros œuvre, fondations, dalles béton et élévation de murs — comme la maison photographiée en vue aérienne dans nos réalisations.",
  },
  {
    ville: "Rouen et la Normandie",
    communes: "Rouen · toute la Normandie",
    texte: "Selon la nature et l'importance du chantier, nous pouvons intervenir jusqu'à Rouen et dans d'autres secteurs de Normandie. Présentez-nous votre projet : nous vous dirons franchement si nous pouvons le prendre en charge.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": ["GeneralContractor", "ProfessionalService"],
  name: "DMZ Normandie",
  description: "Entreprise de maçonnerie et rénovation à Bernay, dans l'Eure (27) et en Normandie : maçonnerie générale, gros œuvre, extensions, fondations, murs et ouvrages en béton.",
  telephone: "+33745049189",
  email: "dmznormandie@gmail.com",
  url: "https://dmznormandie.fr/zones",
  address: { "@type": "PostalAddress", streetAddress: "7 rue Jean de la Fontaine", postalCode: "27000", addressLocality: "Évreux", addressRegion: "Normandie", addressCountry: "FR" },
  areaServed: ["Bernay", "Évreux", "Thiberville", "Beuzeville", "Pont-Audemer", "Conches-en-Ouche", "Louviers", "Deauville", "Fontaine-l'Abbé", "Menneval", "Serquigny", "Beaumont-le-Roger", "Brionne", "Le Neubourg", "Vernon", "Rouen", "Eure", "Normandie"],
};

export default function ZonesIntervention() {
  return (
    <>
      <SEO
        title="Zones d'intervention : Bernay, Évreux, Eure | DMZ Normandie"
        description="Maçonnerie et rénovation à Bernay, Évreux, Beuzeville, Louviers, Conches-en-Ouche, Pont-Audemer, Thiberville, Deauville et en Normandie. Devis gratuit."
        path="/zones"
        jsonLd={jsonLd}
      />
      <PageHero
        title="Nos zones d'intervention"
        subtitle="Basée entre Bernay et Évreux, DMZ Normandie intervient dans toute l'Eure et, selon l'importance du chantier, dans toute la Normandie — pour les particuliers comme pour les professionnels."
      />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {zones.map((z, i) => (
              <Reveal key={z.ville} delay={(i % 2) * 0.06} className="card-dark p-8" data-testid={`zone-card-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <MapPin size={18} className="text-[#C4A484]" />
                  <h2 className="font-serif-display font-light text-2xl text-[#F5F5F0]">{z.ville}</h2>
                </div>
                <p className="text-xs tracking-[0.15em] uppercase text-[#C4A484] mb-4">{z.communes}</p>
                <p className="text-sm font-light text-zinc-400 leading-relaxed">{z.texte}</p>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 border border-white/10 p-8 lg:p-10" data-testid="pages-locales-section">
            <SectionHead overline="Pages locales" title="Nos chantiers et services par ville" />
            <p className="-mt-6 mb-8 max-w-3xl text-sm font-light leading-relaxed text-zinc-400">
              Retrouvez une page dédiée à chaque secteur où un chantier DMZ Normandie est visible : travaux réalisés, photos réelles et réponses aux questions locales.
            </p>
            <RelatedLinks slugs={["maconnerie-beuzeville", "maconnerie-louviers", "maconnerie-conches-en-ouche", "maconnerie-pont-audemer", "maconnerie-thiberville", "maconnerie-deauville"]} />
          </div>

          <div className="mt-20">
            <SectionHead overline="Nos services partout dans l'Eure" title="Les travaux que nous réalisons près de chez vous" />
            <RelatedLinks slugs={["maconnerie-bernay", "maconnerie-evreux", "gros-oeuvre", "renovation-bernay", "extension-maison", "dalles-beton", "ouverture-mur-porteur", "construction-murs", "beton-amenagements-exterieurs", "rejointoiement"]} />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
